/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import { useEffect, useRef, useState } from 'react'
import { GoogleMapsServicesProvider } from '@wiggot/search-components'
import {
  Map,
  Properties,
  TourProvider,
  TourSearchWizzardProvider
} from './components'
import { Filters } from './Filters'
import { useAmplitude } from '@wiggot/analytics/src/providers/amplitud-provider'
import { AmplitudeEvents } from '@/interfaces/enum'
import { FirtsSearch } from '@/components/FirtsSearch'
import { useMapStore, usePropertiesStore } from '@wiggot/search-components/state-library/src'
import { dummyProperty } from '@/utils/constants'
import GoogleIcon from '@dd3tech/wiggui/GoogleIcon'
import useMatchWindowQuery from '@wiggot/web-components/hooks/ds/useMatchWindowQuery'
import { webGLSupported } from '@wiggot/web-components/utils/webGLSupport'
import { toast, ToastProvider } from '@dd3tech/wiggui/Toast'

const gmapsApiKey = process.env.GOOGLE_MAPS_API_KEY || ''
export const Results = () => {
  const { setPropertyLoaderStatus, setFirtsTime, properties, setProperties } =
    usePropertiesStore()
  const [mapboxGLIsSupported, setMapboxGLIsSupported] = useState<
    boolean | null
  >(null)

  const { isMD } = useMatchWindowQuery()
  const { map, drawingTool } = useMapStore()
  const showMapButton = useRef<HTMLButtonElement>(null)
  const [hasCleanFilters, setHasCleanFilters] = useState(false)
  const [showFirtsSearch, setShowFirtsSearch] = useState(true)
  const [animateSearchBar, setAnimateSearchBar] = useState(false)
  const [isShowTour, setIsShowTour] = useState(false)
  const [hiddeCards, setHiddeCards] = useState(false)
  const [hiddeMap, setHiddeMap] = useState(!isMD)
  const { trackEvent, isInitialized } = useAmplitude()

  const onClickSearch = () => {
    setAnimateSearchBar(true)
    setTimeout(() => {
      setAnimateSearchBar(false)
    }, 300)
  }

  const onCleanFilters = () => {
    setHasCleanFilters(true)
  }
  const onFinishTour = () => {
    if (isShowTour) {
      setIsShowTour(false)
    }
    const mapButton = document.getElementById('show-map-button')
    if (mapButton) {
      mapButton.classList.remove('!hidden')
    }
    if (isMD) {
      setHiddeMap(!mapboxGLIsSupported)
      setHiddeCards(false)
    } else {
      setHiddeMap(true)
      setHiddeCards(false)
    }
    if (properties.length === 1 && properties[0].id === 'property-dummy') {
      setShowFirtsSearch(true)
      setProperties([])
    }
  }
  const onClickHiddeMap = () => {
    setHiddeMap(true)
    if (hiddeCards) {
      setHiddeCards(false)
    }
  }
  const onActiveWizard = () => {
    setFirtsTime(true)
    setTimeout(() => {
      setIsShowTour(true)
    }, 200)
    if (isMD) {
      setHiddeMap(!mapboxGLIsSupported)
      setHiddeCards(false)
    } else {
      setHiddeMap(true)
      setHiddeCards(false)
    }
    if (!properties || properties.length === 0) {
      setShowFirtsSearch(false)
      setTimeout(() => {
        setPropertyLoaderStatus({
          loading: false,
          error: false,
          waitingAnimation: false
        })
        setProperties([dummyProperty])
        setFirtsTime(false)
      }, 200)
    }
  }
  const onShowMap = () => {
    if (hiddeMap) {
      setHiddeMap(false)
    }
    if (!isMD && !hiddeCards) {
      setHiddeCards(true)
    }
  }
  const onResizeMap = () => {
    if (!hiddeMap) {
      map?.resize()
    }
  }

  const onClickButtonMapRef = () => {
    setTimeout(() => {
      showMapButton.current?.focus()
    }, 300)
    setTimeout(() => {
      showMapButton.current?.click()
      showMapButton.current?.blur()
    }, 500)
  }
  useEffect(() => {
    map?.resize()
    setTimeout(() => {
      map?.resize()
    }, 100)
    setTimeout(() => {
      map?.resize()
    }, 200)
    setTimeout(() => {
      map?.resize()
    }, 300)
    setTimeout(() => {
      map?.resize()
    }, 400)
  }, [hiddeCards, hiddeMap])

  useEffect(() => {
    setHiddeCards(false)
    if (isMD) {
      setHiddeMap(!mapboxGLIsSupported)
      return
    }
    setHiddeMap(true)
  }, [isMD])

  useEffect(() => {
    const isSupported = webGLSupported()
    setMapboxGLIsSupported(isSupported)
    setHiddeMap(!isMD || !isSupported)
  }, [])

  useEffect(() => {
    if (isInitialized) {
      trackEvent({
        name: AmplitudeEvents.SEARCH_RESULTS_VIEW
      })
    }
  }, [isInitialized])

  useEffect(() => {
    if (drawingTool.status === 'processed') {
      setShowFirtsSearch(false)
    }
  }, [drawingTool])
  return (
    <TourProvider disableKeyboardNavigation={true} onSkipGuide={onFinishTour}>
      <ToastProvider />
      <TourSearchWizzardProvider
        onActiveWizard={onActiveWizard}
        onFinishTour={onFinishTour}
        onClickMap={onClickButtonMapRef}
        mapboxGLIsSupported={Boolean(mapboxGLIsSupported)}
      >
        <GoogleMapsServicesProvider apiKey={gmapsApiKey} />
        <div className="w-full h-full grid grid-cols-1 grid-rows-[76px,auto]">
          <Filters
            animateSearchBar={animateSearchBar}
            setShowFirtsSearch={setShowFirtsSearch}
            hasCleanFilters={hasCleanFilters}
            setHasCleanFilters={setHasCleanFilters}
          />
          <div className="w-full h-full flex overflow-y-hidden  overflow-x-hidden z-0 pb-[100px] md:pb-0">
            {showFirtsSearch && !hiddeCards && (
              <FirtsSearch hiddeMap={hiddeMap} onClickSearch={onClickSearch} />
            )}
            {!showFirtsSearch && (
              <Properties
                hiddeMap={hiddeMap}
                mapboxGLIsSupported={Boolean(mapboxGLIsSupported)}
                hiddeCards={hiddeCards}
                isShowTour={isShowTour}
                onCleanFilters={onCleanFilters}
                onShowMap={onShowMap}
              />
            )}
            {mapboxGLIsSupported && (
              <Map
                hiddeCards={hiddeCards}
                hiddeMap={hiddeMap}
                onClickHiddeCards={() => setHiddeCards((prev) => !prev)}
                onClickHiddeMap={onClickHiddeMap}
                onResizeMap={onResizeMap}
              />
            )}
          </div>
        </div>
        <button
          id="show-map-button"
          ref={showMapButton}
          onClick={() => {
            if (mapboxGLIsSupported) {
              setHiddeCards((prev) => !prev)
              setHiddeMap((prev) => !prev)
              return
            }
            toast({
              type: 'error',
              title: 'No disponible en este navegador.'
            })
          }}
          type="button"
          className="absolute bottom-4 mb-16 md:mb-0 left-1/2 transform -translate-x-1/2 md:hidden flex gap-2 py-[10px] px-5 rounded-full border border-surface-strokes bg-white shadow-[0px_3px_6px_0px_rgba(34,34,34,0.16)] tour-search-step-mb-3 focus:bg-button-secondary-pressed"
        >
          <p className="text-sm font-semibold">
            {hiddeMap ? 'Ver mapa' : 'Ver listado'}
          </p>
          <GoogleIcon
            className="text-xl leading-5"
            name={hiddeMap ? 'map' : 'list'}
          />
        </button>
      </TourSearchWizzardProvider>
    </TourProvider>
  )
}
